import type { AboutUsViewModel } from "src/http/types";
import type { AppContext } from "src/shared/context/AppContext";
import { OsHint } from "src/website-apps/shared/utils/isNative";

// AboutUsViewModel's array fields are readonly (API response contract). This page-local model copy is
// intentionally mutated in place before render, same as the original JS.
type Writable<T> = { -readonly [K in keyof T]: T[K] };
type DownloadBannerModel = Writable<Pick<AboutUsViewModel, "downloadLinks" | "downloadImagesUrl">>;

/**
 * Removes `downloadLinks` and `downloadImagesUrl` by index from the model.
 */
const removeBannersFromModel = (model: DownloadBannerModel, bannerIndex: number) => {
  for (const property of ["downloadLinks", "downloadImagesUrl"] as const) {
    // downloadLinks is nullable at the API layer but always populated on this render path.
    model[property] = model[property]!.filter((_, idx) => idx !== bannerIndex);
  }
};

/**
 * Removes download images URLs depending on OS hint and execution environment.
 *
 * 1. mWeb iOS: removes the google play store banner
 * 2. mWeb Android: removes the app store banner
 * 3. rWeb: leaves both banners
 * @param appContext - App context
 * @param model - The list of app store URLs
 * @param osHint - `useOsHint(appContext)` result, computed by the caller (hooks can't run in here)
 */
export const removeMobileAppDownloadBanners = (appContext: AppContext, model: DownloadBannerModel, osHint: OsHint) => {
  if (appContext.isNativeApp) return;

  if (osHint === OsHint.Ios) {
    // Safari iPhone/iPad; show only App Store
    removeBannersFromModel(
      model,
      model.downloadLinks!.findIndex((url) => url.startsWith("https://play.google.com")),
    );
  }

  if (osHint === OsHint.Android) {
    // Chrome Android/Tablet; show only Google Play Store
    removeBannersFromModel(
      model,
      model.downloadLinks!.findIndex((url) => url.startsWith("https://apps.apple.com")),
    );
  }
};
